import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, STATUS_META, fmtMoney, extractErrorMessage } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Mail, Phone, MapPin, Building2, Calendar, Receipt, CheckCircle2, Circle, ClipboardList } from "lucide-react";

const CLIENT_STATUS_META = {
  invited: { label: "Invited", cls: "border-slate-200 bg-slate-50 text-slate-600" },
  onboarding: { label: "Onboarding", cls: "border-sky-200 bg-sky-50 text-sky-700" },
  agreement_pending: { label: "Agreement Pending", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  billing_pending: { label: "Billing Pending", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  pending_approval: { label: "Pending Approval", cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  active: { label: "Active", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
};

const formatMoney = (amount, currency) => fmtMoney(currency, amount);

// The exact fields the client submits during ARPS registration/onboarding (mirrors ClientOnboarding).
const REG_FIELDS = [
  ["legal_business_name", "Legal business name"],
  ["dba", "DBA"],
  ["entity_type", "Entity type"],
  ["state_of_formation", "State of formation"],
  ["ein", "EIN / Federal Tax ID"],
  ["state_tax_id", "State registration / tax ID"],
  ["professional_license", "Professional license"],
  ["website", "Website"],
  ["physical_address", "Physical address"],
  ["mailing_address", "Mailing address"],
  ["billing_address", "Billing address"],
  ["primary_contact_name", "Primary contact"],
  ["primary_contact_email", "Primary contact email"],
  ["primary_contact_phone", "Primary contact phone"],
  ["billing_contact", "Billing / AP contact"],
  ["payment_terms", "Payment terms"],
];

export default function AdminClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [regOpen, setRegOpen] = useState(false);
  const [reg, setReg] = useState(null);
  const [regBusy, setRegBusy] = useState(false);

  const load = () => api.get(`/clients/${id}/detail`).then(({ data }) => setD(data)).catch(() => toast.error("Failed to load client"));
  // Reset the cached registration record whenever the client id changes, then reload — prevents
  // showing a previously-opened client's submission (no cross-client data exposure).
  useEffect(() => { setReg(null); setRegOpen(false); load(); /* eslint-disable-next-line */ }, [id]);

  // Always fetch fresh for the CURRENT client id on open, so the dialog can never show another client's data.
  const openRegistration = async () => {
    setRegOpen(true); setReg(null); setRegBusy(true);
    try { const { data } = await api.get(`/admin/clients/${id}/onboarding`); setReg(data); }
    catch { toast.error("Failed to load registration submission"); }
    finally { setRegBusy(false); }
  };

  if (!d) return <p className="text-slate-400">Loading…</p>;
  const p = d.profile;
  const s = d.summary;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="client-detail-page">
      <button data-testid="back-to-clients" onClick={() => nav("/admin/clients")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back to clients
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="client-detail-name">{p.firm_name || p.name}</h2>
          <p className="text-sm text-slate-500">{p.firm_name ? `Contact: ${p.name}` : p.email}</p>
        </div>
        {(() => { const m = CLIENT_STATUS_META[p.client_status] || CLIENT_STATUS_META.active; return (
          <div className="flex items-center gap-3">
            <Button data-testid="view-registration-btn" variant="outline" size="sm" onClick={openRegistration}>
              <ClipboardList className="mr-1.5 h-4 w-4" /> View Registration
            </Button>
            <span data-testid="client-status-badge" className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${m.cls}`}>{m.label}</span>
          </div>
        ); })()}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="client-order-summary">
        <Metric label="Total" value={s.total} />
        <Metric label="Active" value={s.active} highlight />
        <Metric label="Pending" value={s.pending} />
        <Metric label="Completed" value={s.completed} />
        <Metric label="Failed / Unable" value={s.failed} />
        <Metric label="Overdue" value={s.overdue} danger={s.overdue > 0} />
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="billing-terms" data-testid="tab-billing-terms">Billing terms & onboarding</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="section-client-profile">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field icon={Building2} label="Client / organization" value={p.firm_name || "Independent"} />
              <Field icon={Building2} label="Contact person" value={p.name} />
              <Field icon={Mail} label="Email" value={p.email} />
              <Field icon={Phone} label="Phone" value={p.phone || "—"} />
              <Field icon={MapPin} label="Address" value={p.address || "—"} />
              <Field icon={Calendar} label="Registered" value={p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="section-client-orders">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Job #</th><th className="px-4 py-2.5">Defendant</th>
                  <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Server</th>
                  <th className="px-4 py-2.5">Deadline</th><th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Attempts</th><th className="px-4 py-2.5">Proof</th>
                  <th className="px-4 py-2.5">Created</th>
                </tr>
              </thead>
              <tbody>
                {d.orders.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">This client has no orders yet.</td></tr>}
                {d.orders.map((o) => (
                  <tr key={o.id} data-testid={`client-order-${o.job_number}`} className="cursor-pointer border-t border-slate-50 hover:bg-slate-50" onClick={() => nav(`/admin/jobs/${o.id}`)}>
                    <td className="px-4 py-2.5 font-semibold text-slate-900">#{o.job_number}</td>
                    <td className="px-4 py-2.5 text-slate-700">{o.defendant}</td>
                    <td className="px-4 py-2.5 text-slate-600">{o.service_type}</td>
                    <td className="px-4 py-2.5 text-slate-600">{o.assigned_server_name || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{o.deadline || "—"}</td>
                    <td className="px-4 py-2.5">{STATUS_META[o.status]?.label || o.status}{o.overdue && <span className="ml-1 text-[10px] font-bold text-red-600">OVERDUE</span>}</td>
                    <td className="px-4 py-2.5 text-slate-700">{o.attempts}</td>
                    <td className="px-4 py-2.5 text-slate-600">{o.proof_status ? (o.proof_status === "approved" ? "Approved" : "Pending") : "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="billing">
          <div className="space-y-4" data-testid="section-client-billing">
            <div className="grid grid-cols-2 gap-3">
              <Metric label="Total billed" value={formatMoney(d.billing.total_billed, d.billing.currency)} />
              <Metric label="Outstanding" value={formatMoney(d.billing.outstanding, d.billing.currency)} danger={d.billing.outstanding > 0} />
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-2.5">Invoice</th><th className="px-4 py-2.5">Job #</th><th className="px-4 py-2.5">Total</th><th className="px-4 py-2.5">Paid</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Created</th></tr>
                </thead>
                <tbody>
                  {d.billing.invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No invoices for this client yet.</td></tr>}
                  {d.billing.invoices.map((inv, i) => (
                    <tr key={i} data-testid={`client-invoice-${i}`} className="border-t border-slate-50">
                      <td className="px-4 py-2.5 font-semibold text-slate-900">{inv.invoice_number}</td>
                      <td className="px-4 py-2.5 text-slate-600">#{inv.job_number}</td>
                      <td className="px-4 py-2.5 text-slate-700">{formatMoney(inv.total, inv.currency)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{formatMoney(inv.amount_paid, inv.currency)}</td>
                      <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${inv.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : inv.payment_status === "partial" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{inv.payment_status}</span></td>
                      <td className="px-4 py-2.5 text-slate-500">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="billing-terms">
          <ClientBillingTerms clientId={id} onChange={load} />
        </TabsContent>

        <TabsContent value="activity">
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="section-client-activity">
            {d.activity.length === 0 && <p className="py-6 text-center text-slate-400">No activity recorded.</p>}
            <div className="space-y-2">
              {d.activity.map((a, i) => (
                <div key={i} data-testid={`client-activity-${i}`} className="flex items-start justify-between gap-3 border-b border-slate-50 py-2 text-sm last:border-0">
                  <div>
                    <span className="font-medium text-slate-900">{a.detail || (a.event || "").replace(/_/g, " ")}</span>
                    {a.job_number && <span className="ml-2 text-xs text-slate-400">#{a.job_number}</span>}
                  </div>
                  <span className="whitespace-nowrap text-xs text-slate-400">{a.by ? `${a.by} · ` : ""}{a.at ? new Date(a.at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent data-testid="registration-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="registration-dialog-title">
              Registration submission — {p.firm_name || p.name}
            </DialogTitle>
            <DialogDescription>Read-only record submitted by this client during registration.</DialogDescription>
          </DialogHeader>
          {regBusy && <p className="py-6 text-center text-sm text-slate-400">Loading submission…</p>}
          {!regBusy && reg && (
            <div className="space-y-5">
              <p className="text-xs text-slate-500">Information this client submitted during registration. Review before approval.</p>
              <RegSection title="Account">
                <RegRow testid="reg-account-name" label="Contact name" value={p.name} />
                <RegRow testid="reg-account-email" label="Email" value={p.email} />
                <RegRow testid="reg-account-phone" label="Phone" value={p.phone} />
                <RegRow testid="reg-account-registered" label="Registered" value={p.created_at ? new Date(p.created_at).toLocaleString() : ""} />
                <RegRow testid="reg-account-status" label="Status" value={(CLIENT_STATUS_META[reg.client_status] || {}).label || reg.client_status} />
              </RegSection>
              <RegSection title="Business / registration details">
                {REG_FIELDS.map(([k, label]) => (
                  <RegRow key={k} testid={`reg-${k}`} label={label} value={reg.business_profile?.[k]} />
                ))}
              </RegSection>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RegSection({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">{title}</p>
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function RegRow({ label, value, testid }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p data-testid={testid} className="mt-0.5 break-words text-sm font-medium text-slate-900">{value || "—"}</p>
    </div>
  );
}

function ReadyRow({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
      <span className={ok ? "text-slate-800" : "text-slate-500"}>{label}</span>
    </div>
  );
}

function ClientBillingTerms({ clientId, onChange }) {
  const [ob, setOb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ service_fee: "", print_fee_per_page: "", rush_fee: "0", mileage_rate: "0", currency: "USD", compensation_type: "percentage", compensation_value: "", effective_date: "", expiration_date: "" });

  const loadOb = () => api.get(`/admin/clients/${clientId}/onboarding`).then(({ data }) => {
    setOb(data);
    const t = data.billing_terms;
    if (t) setF({ service_fee: String(t.service_fee), print_fee_per_page: String(t.print_fee_per_page), rush_fee: String(t.rush_fee), mileage_rate: String(t.mileage_rate), currency: t.currency, compensation_type: t.compensation_type, compensation_value: String(t.compensation_value), effective_date: (t.effective_date || "").slice(0, 10), expiration_date: (t.expiration_date || "").slice(0, 10) });
  }).catch(() => {});
  useEffect(() => { loadOb(); /* eslint-disable-next-line */ }, [clientId]);

  if (!ob) return <p className="text-slate-400">Loading…</p>;
  const r = ob.readiness;
  const serverPrint = (parseFloat(f.print_fee_per_page || 0) * 0.7);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/clients/${clientId}/billing-terms`, {
        service_fee: parseFloat(f.service_fee || 0), print_fee_per_page: parseFloat(f.print_fee_per_page || 0),
        rush_fee: parseFloat(f.rush_fee || 0), mileage_rate: parseFloat(f.mileage_rate || 0),
        currency: f.currency, compensation_type: f.compensation_type, compensation_value: parseFloat(f.compensation_value || 0),
        effective_date: f.effective_date ? new Date(f.effective_date).toISOString() : null,
        expiration_date: f.expiration_date ? new Date(f.expiration_date).toISOString() : null,
      });
      toast.success("Negotiated billing terms saved"); await loadOb(); onChange && onChange();
    } catch (e) { toast.error(extractErrorMessage(e, "Failed to save billing terms")); }
    finally { setBusy(false); }
  };
  const setContractStatus = async (approval_status) => {
    setBusy(true);
    try { await api.post(`/admin/clients/${clientId}/billing-terms/status`, { approval_status }); toast.success(`Contract ${approval_status}`); await loadOb(); onChange && onChange(); }
    catch (e) { toast.error(extractErrorMessage(e, "Failed to update contract status")); }
    finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true);
    try { await api.post(`/admin/clients/${clientId}/activate`); toast.success("Client activated"); await loadOb(); onChange && onChange(); }
    catch (e) { toast.error(extractErrorMessage(e, "Cannot activate client yet")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="section-client-billing-terms">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h4 className="font-display text-base font-bold text-slate-900">Onboarding checklist</h4>
        <div className="mt-3 space-y-2">
          <ReadyRow ok={r.info_complete} label="Client information complete" />
          <ReadyRow ok={r.business_profile_complete} label="Business profile complete (legal name, entity type, contact, billing address)" />
          <ReadyRow ok={r.documents_accepted} label={`All onboarding documents accepted (${(ob.documents || []).filter((x) => x.accepted).length}/${(ob.documents || []).length})`} />
          <ReadyRow ok={r.billing_configured} label="Negotiated billing terms configured (optional — standard clients pay per order)" />
        </div>
        {(ob.pending_review || []).length > 0 && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="pending-review-summary">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Assignment ready — pricing & compensation applied automatically</p>
            {ob.pending_review.map((p) => (
              <div key={p.order_id} data-testid={`pending-review-${p.job_number}`} className="mt-2 space-y-1.5 border-t border-emerald-100 pt-2 first:border-0">
                <ReadyRow ok={p.assignment_complete} label={`Assignment complete — #${p.job_number} · ${p.defendant} · ${p.service_type}`} />
                <ReadyRow ok={p.client_price != null} label={`Standard price automatically determined${p.client_price != null ? `: ${p.currency} $${Number(p.client_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ""}`} />
                <ReadyRow ok={p.server_compensation != null} label={`Server compensation automatically determined${p.server_compensation != null ? `: ${p.currency} $${Number(p.server_compensation).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ""}`} />
              </div>
            ))}
            <p className="mt-2 text-[11px] text-emerald-700/80">No manual fee entry required — ARPS Standard pricing & compensation are authoritative. Approving flips this assignment to awaiting payment.</p>
          </div>
        )}
        <Button data-testid="activate-client-btn" onClick={activate} disabled={busy || ob.client_status === "active" || !(r.info_complete && r.business_profile_complete && r.documents_accepted)}
          className="mt-4 bg-emerald-600 text-white hover:bg-emerald-700">
          {ob.client_status === "active" ? "Client is active" : "Approve & activate client"}
        </Button>
        {ob.client_status !== "active" && !(r.info_complete && r.business_profile_complete && r.documents_accepted) && (
          <p className="mt-2 text-xs text-slate-400">Complete client information, business profile, and onboarding documents to enable approval. Negotiated billing terms are optional (standard clients pay per order).</p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h4 className="font-display text-base font-bold text-slate-900">Negotiated client rates</h4>
        <p className="mt-0.5 text-xs text-slate-500">Enter the terms agreed with this client. $0 is a valid value. Saving creates a new version; historical orders keep their original rates.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Labeled label="Currency">
            <select data-testid="ct-currency" value={f.currency} onChange={set("currency")} className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
              {["USD", "CAD", "EUR", "GBP", "AUD"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Labeled>
          <Labeled label="Service fee (client)"><Input data-testid="ct-service-fee" type="number" step="0.01" min="0" value={f.service_fee} onChange={set("service_fee")} /></Labeled>
          <Labeled label="Print fee per page (client)"><Input data-testid="ct-print-fee" type="number" step="0.01" min="0" value={f.print_fee_per_page} onChange={set("print_fee_per_page")} /></Labeled>
          <Labeled label="Server print reimbursement (auto = 70%)">
            <div data-testid="ct-server-print" className="flex h-9 items-center rounded-md border border-slate-100 bg-slate-50 px-2 text-sm text-slate-600">{f.currency} {serverPrint.toFixed(2)}/page</div>
          </Labeled>
          <Labeled label="Rush fee (client)"><Input data-testid="ct-rush-fee" type="number" step="0.01" min="0" value={f.rush_fee} onChange={set("rush_fee")} /></Labeled>
          <Labeled label="Mileage rate (client)"><Input data-testid="ct-mileage" type="number" step="0.01" min="0" value={f.mileage_rate} onChange={set("mileage_rate")} /></Labeled>
          <Labeled label="Server compensation type">
            <select data-testid="ct-comp-type" value={f.compensation_type} onChange={set("compensation_type")} className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
              <option value="percentage">Percentage of service fee</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </Labeled>
          <Labeled label={f.compensation_type === "percentage" ? "Compensation percent (%)" : "Compensation amount"}>
            <Input data-testid="ct-comp-value" type="number" step="0.01" min="0" value={f.compensation_value} onChange={set("compensation_value")} />
          </Labeled>
          <Labeled label="Effective date"><Input data-testid="ct-effective-date" type="date" value={f.effective_date} onChange={set("effective_date")} /></Labeled>
          <Labeled label="Expiration date (blank = none)"><Input data-testid="ct-expiration-date" type="date" value={f.expiration_date} onChange={set("expiration_date")} /></Labeled>
        </div>
        <Button data-testid="save-billing-terms-btn" onClick={save} disabled={busy} className="mt-4">Save billing terms</Button>
        {ob.billing_terms && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="contract-status-box">
            <p className="text-slate-600">Active v{ob.billing_terms.version} · approval:{" "}
              <span className={`font-semibold ${ob.billing_terms.approval_status === "approved" ? "text-emerald-600" : "text-amber-600"}`} data-testid="contract-approval-status">{ob.billing_terms.approval_status}</span>
              {" "}· <span className={ob.billing_terms.enforceable ? "text-emerald-600" : "text-slate-400"}>{ob.billing_terms.enforceable ? "enforceable now" : "not enforceable (client uses standard upfront pricing)"}</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="contract-approve-btn" disabled={busy} onClick={() => setContractStatus("approved")}>Approve</Button>
              <Button size="sm" variant="outline" data-testid="contract-suspend-btn" disabled={busy} onClick={() => setContractStatus("suspended")}>Suspend</Button>
              <Button size="sm" variant="outline" data-testid="contract-reject-btn" disabled={busy} onClick={() => setContractStatus("rejected")}>Reject</Button>
            </div>
            <p className="mt-2 text-slate-400">Server print reimbursement is always 70% of the client print rate. If a contract is suspended/rejected or outside its date window, the client automatically reverts to standard pricing and upfront payment.</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="executed-agreements">
        <h4 className="font-display text-base font-bold text-slate-900">Executed agreements &amp; documents</h4>
        <p className="mt-0.5 text-xs text-slate-500">Signed onboarding documents. View or download the executed PDF.</p>
        <div className="mt-3 space-y-2">
          {(ob.documents || []).filter((d) => d.accepted).length === 0 && <p className="text-sm text-slate-400">No executed documents yet.</p>}
          {(ob.documents || []).filter((d) => d.accepted).map((d) => (
            <div key={d.key} data-testid={`exec-agreement-${d.key}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">{d.title} <span className="text-xs font-normal text-slate-400">v{d.version}</span></p>
                <p className="text-xs text-slate-400">Signed by {d.signer || d.accepting_user} · {d.accepted_at ? new Date(d.accepted_at).toLocaleString() : ""}</p>
              </div>
              {d.document_id && (
                <Button data-testid={`exec-agreement-view-${d.key}`} variant="outline" size="sm" onClick={async () => {
                  try { const r = await api.get(`/files/${d.document_id}`, { responseType: "blob" }); const u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000); }
                  catch { toast.error("Unable to open document"); }
                }}>View / download</Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      {children}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 flex items-center gap-2 text-sm font-medium text-slate-900"><Icon className="h-4 w-4 text-slate-400" /> {value}</p>
    </div>
  );
}

function Metric({ label, value, highlight, danger }) {
  return (
    <div className={`rounded-xl border p-4 ${danger ? "border-red-200 bg-red-50" : highlight ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}>
      <p className={`font-display text-2xl font-bold ${danger ? "text-red-700" : highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
      <p className="mt-0.5 text-xs text-slate-500">{label}</p>
    </div>
  );
}
